import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import {
  fetchToegangscontroles,
  toggleToegangscontrole,
  testToegangscontrole,
  testDoorsyConnection,
  createToegangsCode
} from '../Admin/Toegangscontrole/Reducers/actions';
import CodeFormulier from './Components/CodeFormulier';
import ToegangsCodesOverzicht from './Components/ToegangsCodesOverzicht';
import moment from 'moment';

const ToegangscontroleIndex = ({
  toegangscontroles,
  fetchToegangscontroles,
  toggleToegangscontrole,
  testToegangscontrole,
  testDoorsyConnection,
  createToegangsCode,
  error
}) => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('reserveringen'); // 'reserveringen', 'codes'
  const [codeLoading, setCodeLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    await fetchToegangscontroles();
    setLoading(false);
  };

  const getDoorsyConfig = () => {
    return toegangscontroles.find(t => t.naam === 'doorsy');
  };

  const handleTestConnection = async () => {
    const doorsyConfig = getDoorsyConfig();
    if (doorsyConfig) {
      await testDoorsyConnection();
    }
  };

  const handleToggleSystem = async () => {
    const doorsyConfig = getDoorsyConfig();
    if (doorsyConfig) {
      await toggleToegangscontrole(doorsyConfig._id);
      await loadData(); // Herlaad data
    }
  };

  const handleCreateCode = async (codeData) => {
    setCodeLoading(true);
    try {
      const result = await createToegangsCode(codeData);

      if (result.success) {
        const code = codeData.pins[0]; // De gegenereerde 6-cijfer code
        alert(`Toegangscode ${code} succesvol aangemaakt via Doorsy!\nGeldig van ${moment(codeData.startDate).format('DD-MM-YYYY')} tot ${moment(codeData.endDate).format('DD-MM-YYYY')} (exclusief)\n\nDoorcy ID: ${codeData.doorcyId}\nScene ID: ${codeData.sceneId}`);
      } else {
        throw new Error(result.error || 'Onbekende fout');
      }

    } catch (error) {
      console.error('Error creating access code:', error);
      alert('Er is een fout opgetreden bij het aanmaken van de toegangscode: ' + (error.message || error));
    } finally {
      setCodeLoading(false);
    }
  };

  const getStatusBadge = (config) => {
    if (!config) return <span className="badge bg-secondary">Niet geconfigureerd</span>;
    
    switch (config.status) {
      case 'niet_geconfigureerd':
        return <span className="badge bg-secondary">Niet geconfigureerd</span>;
      case 'geconfigureerd':
        return <span className="badge bg-warning">Geconfigureerd</span>;
      case 'geautoriseerd':
        return <span className="badge bg-success">Geautoriseerd</span>;
      case 'fout':
        return <span className="badge bg-danger">Fout</span>;
      default:
        return <span className="badge bg-secondary">Onbekend</span>;
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Laden...</span>
        </div>
      </div>
    );
  }

  const doorsyConfig = getDoorsyConfig();
  const isDoorsyActive = doorsyConfig?.actief;
  const isDoorsyConfigured = doorsyConfig?.status !== 'niet_geconfigureerd';

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>
              <i className="fas fa-key me-2 text-primary"></i>
              Toegangscontrole
            </h2>
            <div>
              {doorsyConfig && (
                <button
                  className={`btn btn-sm ${isDoorsyActive ? 'btn-warning' : 'btn-success'} me-2`}
                  onClick={handleToggleSystem}
                >
                  <i className={`fas ${isDoorsyActive ? 'fa-pause' : 'fa-play'} me-1`}></i>
                  {isDoorsyActive ? 'Uitschakelen' : 'Inschakelen'}
                </button>
              )}
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={handleTestConnection}
                disabled={!isDoorsyActive}
              >
                <i className="fas fa-vial me-1"></i>
                Test Verbinding
              </button>
            </div>
          </div>

          {/* Status Card */}
          <div className="card mb-4">
            <div className="card-body">
              <div className="row align-items-center">
                <div className="col-md-3">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-door-closed fa-2x text-info me-3"></i>
                    <div>
                      <h5 className="mb-0">RecraPlan Systeem</h5>
                      <small className="text-muted">Toegangscontrole Platform</small>
                    </div>
                  </div>
                </div>
                <div className="col-md-2">
                  <small className="text-muted d-block">Status</small>
                  <div>
                    {getStatusBadge(doorsyConfig)}
                    {isDoorsyActive && (
                      <span className="badge bg-success ms-1">Actief</span>
                    )}
                  </div>
                </div>
                <div className="col-md-3">
                  <small className="text-muted d-block">Laatste Update</small>
                  <strong>
                    {doorsyConfig ? moment(doorsyConfig.bijgewerkt).fromNow() : 'Nooit'}
                  </strong>
                </div>
                <div className="col-md-2">
                  <small className="text-muted d-block">API Base URL</small>
                  <small className="font-monospace">
                    {doorsyConfig?.configuratie?.api_base_url || 'Niet geconfigureerd'}
                  </small>
                </div>
                <div className="col-md-2">
                  <small className="text-muted d-block">Auth Type</small>
                  <span className="badge bg-info">
                    {doorsyConfig?.configuratie?.auth_type?.replace('_', ' ').toUpperCase() || 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="alert alert-danger mb-4">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
            </div>
          )}

          {/* System not configured alert */}
          {!isDoorsyConfigured && (
            <div className="alert alert-warning mb-4">
              <i className="fas fa-exclamation-circle me-2"></i>
              <strong>Systeem niet geconfigureerd!</strong> 
              Ga naar Admin → Toegangscontrole om RecraPlan te configureren.
            </div>
          )}

          {/* Navigation Tabs - Sneat Style */}
          {isDoorsyActive && (
            <>
              <div className="nav-align-top mb-4">
                <ul className="nav nav-pills nav-fill gap-2 p-3" role="tablist" style={{ backgroundColor: '#f8f9fa', borderRadius: '10px' }}>
                  <li className="nav-item" role="presentation">
                    <button
                      className={`nav-link ${activeTab === 'reserveringen' ? 'active' : ''}`}
                      style={{
                        borderRadius: '8px',
                        transition: 'all 0.2s ease',
                        backgroundColor: activeTab === 'reserveringen' ? '#d96a29' : 'transparent',
                        color: activeTab === 'reserveringen' ? 'white' : '#6c757d',
                        border: 'none',
                        padding: '0.75rem 1.5rem',
                        fontWeight: '500',
                        boxShadow: activeTab === 'reserveringen' ? '0 2px 4px rgba(217, 106, 41, 0.3)' : 'none'
                      }}
                      onClick={() => setActiveTab('reserveringen')}
                      type="button"
                      role="tab"
                    >
                      <i className="fas fa-list me-2"></i>
                      Overzicht Codes
                    </button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button
                      className={`nav-link ${activeTab === 'codes' ? 'active' : ''}`}
                      style={{
                        borderRadius: '8px',
                        transition: 'all 0.2s ease',
                        backgroundColor: activeTab === 'codes' ? '#d96a29' : 'transparent',
                        color: activeTab === 'codes' ? 'white' : '#6c757d',
                        border: 'none',
                        padding: '0.75rem 1.5rem',
                        fontWeight: '500',
                        boxShadow: activeTab === 'codes' ? '0 2px 4px rgba(217, 106, 41, 0.3)' : 'none'
                      }}
                      onClick={() => setActiveTab('codes')}
                      type="button"
                      role="tab"
                    >
                      <i className="fas fa-key me-2"></i>
                      Nieuwe Code
                    </button>
                  </li>
                </ul>
              </div>

              {/* Tab Content */}
              <div className="tab-content">
                {/* Overzicht Codes Tab */}
                {activeTab === 'reserveringen' && (
                  <div className="row">
                    <div className="col-12">
                      <ToegangsCodesOverzicht />
                    </div>
                  </div>
                )}

                {/* Nieuwe Code Tab */}
                {activeTab === 'codes' && (
                  <div className="row">
                    <div className="col-12">
                      <CodeFormulier
                        onSubmit={handleCreateCode}
                        loading={codeLoading}
                        doorsyConfig={doorsyConfig}
                      />
                    </div>
                  </div>
                )}

              </div>
            </>
          )}

          {/* System inactive message */}
          {isDoorsyConfigured && !isDoorsyActive && (
            <div className="alert alert-info text-center py-5">
              <i className="fas fa-power-off fa-3x text-muted mb-3"></i>
              <h4>Toegangscontrole Systeem Uitgeschakeld</h4>
              <p className="text-muted">Klik op "Inschakelen" om het systeem te activeren.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  toegangscontroles: state.toegangscontrole?.toegangscontroles || [],
  error: state.toegangscontrole?.error || state.toegangscontrole?.doorsy?.error
});

export default connect(mapStateToProps, {
  fetchToegangscontroles,
  toggleToegangscontrole,
  testToegangscontrole,
  testDoorsyConnection,
  createToegangsCode
})(ToegangscontroleIndex);